import { useEffect, useState } from "react";
import { collection, query, orderBy, onSnapshot, doc, deleteDoc, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import { CLUB_ID, THEME_COLOR } from "./config/club";
import CreateReservationModal from "./components/admin/CreateReservationModal";
import ReservationDetailsModal from "./components/admin/ReservationDetailsModal";
import EditReservationModal from "./components/admin/EditReservationModal";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(":");
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));

    return new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    }).format(date);
};

const formatDate = (dateStr) => {
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
    }).format(new Date(dateStr));
};

export default function Reservations() {
    const [reservations, setReservations] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedReservation, setSelectedReservation] = useState(null);
    const [editReservation, setEditReservation] = useState(null);
    const navigate = useNavigate();
    const themeAlpha = (opacity) => `${THEME_COLOR}${opacity}`;

    useEffect(() => {
        const q = query(
            collection(db, "clubs", CLUB_ID, "reservations"),
            orderBy("createdAt", "desc")
        );

        let playerUnsubs = [];

        const unsubReservations = onSnapshot(q, (snap) => {
            const list = snap.docs.map((d) => ({
                id: d.id,
                ...d.data(),
            }));

            playerUnsubs.forEach((u) => u());
            playerUnsubs = [];

            list.forEach((r) => {
                const playersRef = collection(db, "clubs", CLUB_ID, "reservations", r.id, "players");
                const unsubPlayers = onSnapshot(playersRef, (playersSnap) => {
                    const players = playersSnap.docs.map((d) => d.data());
                    const reservedCount = players.filter((p) => p.status === "reserved").length;

                    setReservations((prev) =>
                        prev.map((item) =>
                            item.id === r.id ? { ...item, reservedCount } : item
                        )
                    );
                });
                playerUnsubs.push(unsubPlayers);
            });
            setReservations(list);
        });

        return () => {
            unsubReservations();
            playerUnsubs.forEach((u) => u());
        };
    }, []);

    const totalActivePlayers = reservations.reduce((acc, r) => acc + (r.reservedCount || 0), 0);
    const handleDelete = async (reservation) => {
        const confirm = await Swal.fire({
            title: "Delete reservation?",
            text: "This will remove all players and cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
        });

        if (!confirm.isConfirmed) return;

        try {
            const playersRef = collection(
                db,
                "clubs",
                CLUB_ID,
                "reservations",
                reservation.id,
                "players"
            );

            const playersSnap = await getDocs(playersRef);

            await Promise.all(
                playersSnap.docs.map((docSnap) => deleteDoc(docSnap.ref))
            );

            await deleteDoc(
                doc(db, "clubs", CLUB_ID, "reservations", reservation.id)
            );

            Swal.fire("Deleted", "Reservation and players removed", "success");
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Failed to delete", "error");
        }
    };
    return (
        <div className="container py-4" style={{ maxWidth: "1100px" }}>
            <div className="mb-4">
                <button
                    onClick={() => navigate("/admin/dashboard")}
                    className="btn d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill border-0 shadow-sm transition-all"
                    style={{ backgroundColor: `${THEME_COLOR}25`, color: `${THEME_COLOR}` }}
                >
                    <i className="bi bi-arrow-left-short fs-4"></i>
                    <span className="fw-bold small">Back to Dashboard</span>
                </button>
            </div>
            {/* ADMIN HEADER */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                <div>
                    <h2 className="fw-black mb-1" style={{ color: "var(--text-color)" }}>Reservation Manager</h2>
                    <p className="text-muted small m-0">Control booking flow and session transitions</p>
                </div>
                <button
                    className="btn btn-lg px-4 rounded-pill fw-bold text-white shadow-sm"
                    style={{ backgroundColor: THEME_COLOR }}
                    onClick={() => setShowModal(true)}
                >
                    <i className="bi bi-plus-circle-fill me-2"></i> Create New Slot
                </button>
            </div>

            {/* QUICK STATS ROW */}
            <div className="row g-3 mb-5">
                <div className="col-md-4">
                    <div className="bento-card p-3 border-0 bg-surface">
                        <span className="rank-label">Total Reservations</span>
                        <h3 className="fw-black mb-0">{reservations.length}</h3>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="bento-card p-3 border-0 bg-surface">
                        <span className="rank-label">Active Bookings</span>
                        <h3 className="fw-black mb-0" style={{ color: THEME_COLOR }}>{totalActivePlayers}</h3>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="bento-card p-3 border-0 bg-surface">
                        <span className="rank-label">System Status</span>
                        <h3 className="fw-black mb-0 text-success">Live</h3>
                    </div>
                </div>
            </div>

            {/* TABLE-STYLE HEADERS */}
            <div className="px-4 d-none d-md-flex row mb-2 text-muted small fw-bold text-uppercase tracking-wider">
                <div className="col-4">Session Info</div>
                <div className="col-3">Capacity</div>
                <div className="col-2">Status</div>
                <div className="col-3 text-end">Management</div>
            </div>

            {/* LIST */}
            <div className="d-flex flex-column gap-3">
                {reservations.length === 0 ? (
                    <div className="bento-card p-5 text-center bg-surface border-dashed">
                        <i className="bi bi-calendar-x text-muted h1"></i>
                        <p className="text-muted mt-2">No data available.</p>
                    </div>
                ) : (
                    reservations.map((r) => {
                        const used = r.reservedCount || 0;
                        const max = r.maxSlots || 0;
                        const progress = (used / max) * 100;

                        return (
                            <div key={r.id} className="bento-card p-3 shadow-sm border-0 position-relative overflow-hidden bg-card">
                                {/* Subtle progress background indicator */}
                                <div className="position-absolute bottom-0 start-0 bg-primary opacity-10" style={{ height: '4px', width: `${progress}%`, transition: 'width 0.5s ease', backgroundColor: THEME_COLOR }}></div>

                                <div className="row align-items-center g-3">
                                    {/* INFO */}
                                    <div className="col-md-4">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="rounded-4 d-flex align-items-center justify-content-center" style={{ width: 45, height: 45, background: themeAlpha("10"), color: THEME_COLOR }}>
                                                <i className="bi bi-calendar-check-fill h5 mb-0"></i>
                                            </div>
                                            <div>
                                                <h6 className="fw-bold mb-0">{r.name}</h6>
                                                <small className="text-muted">{formatDate(r.date)} • {`${formatTime(r.startTime)} - ${formatTime(r.endTime)}`}</small>
                                            </div>
                                        </div>
                                    </div>

                                    {/* CAPACITY */}
                                    <div className="col-md-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="flex-grow-1">
                                                <div className="progress rounded-pill" style={{ height: 6, background: 'var(--border-color)' }}>
                                                    <div className="progress-bar rounded-pill" style={{ width: `${progress}%`, backgroundColor: THEME_COLOR }}></div>
                                                </div>
                                            </div>
                                            <span className="small fw-bold">{used}/{max}</span>
                                        </div>
                                    </div>

                                    {/* STATUS */}
                                    <div className="col-md-2">
                                        <span className={`badge rounded-pill px-3 py-2 text-capitalize ${r.status === 'open' ? 'bg-success-subtle text-success' :
                                            r.status === 'full' ? 'bg-warning-subtle text-warning' : 'bg-secondary-subtle text-secondary'
                                            }`}>
                                            {r.status || 'unknown'}
                                        </span>
                                    </div>

                                    {/* ACTIONS */}
                                    <div className="col-md-3 text-end">
                                        <div className="d-flex gap-2 justify-content-md-end">
                                            <button
                                                className="theme-toggle-btn border-0 shadow-none"
                                                style={{ width: 35, height: 35 }}
                                                onClick={() => setSelectedReservation(r)}
                                                title="View Details"
                                            >
                                                <i className="bi bi-eye"></i>
                                            </button>
                                            <button
                                                className="theme-toggle-btn border-0 shadow-none"
                                                style={{ width: 35, height: 35 }}
                                                onClick={() => setEditReservation(r)}
                                                title="Edit"
                                            >
                                                <i className="bi bi-pencil"></i>
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-danger border-0 rounded-pill"
                                                onClick={() => handleDelete(r)}
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <CreateReservationModal show={showModal} onClose={() => setShowModal(false)} />
            <ReservationDetailsModal show={!!selectedReservation} reservationId={selectedReservation?.id} onClose={() => setSelectedReservation(null)} />
            <EditReservationModal
                show={!!editReservation}
                reservation={editReservation}
                onClose={() => setEditReservation(null)}
            />
        </div>
    );
}